import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import {
  fetchWebsitePromotions,
  trackWebsitePromotionEvent,
} from "../api/websitePromotionsApi";
import { getAccessToken, getStoredUser } from "../utils/authTokens";
import {
  DAY_IN_MS,
  getCampaignKey,
  getDismissKey,
  getFrequencyKey,
  getFrequencyStoreType,
  getPromotionId,
  isPromotionDismissed,
  isPromotionFrequencyCapped,
  normalizeTimestamps,
  resolveFrequencyWindowMs,
} from "../utils/websitePromotionDelivery";
import { isPromotionPlacementEnabled } from "../utils/websitePromotionRollout";
import { AUTH_EVENT, getActiveSession } from "../utils/nightStayAccount";
import { getOrCreateVisitorId } from "../utils/portalVisitor";
import {
  DEFAULT_ROTATION_WINDOW_DAYS,
  orderRotatableItemsForVisitor,
  readRotationStore,
  recordRotatableItemExposure,
  writeRotationStore,
} from "../utils/visitorContentRotation";
import { useLanguage } from "./LanguageContext";
import { useWebsiteConfig } from "./WebsiteConfigContext";

import { useHomepageBootstrap, useHomepageHydration } from "./HomepageRenderContext";

const WebsitePromotionsContext = createContext(null);
const LOCAL_STORE_KEY = "ecotour:portal:promotions:local";
const SESSION_STORE_KEY = "ecotour:portal:promotions:session";
const SESSION_ID_KEY = "ecotour:portal:promotions:session_id";

const EMPTY_PLACEMENTS = {
  "page-banner": [],
  "slide-up": [],
  "modal-popup": [],
};

const EMPTY_PROMOTION_STORE = {
  impressions: {},
  dismissals: {},
};

const resolveAuthState = () =>
  getActiveSession() || getAccessToken() ? "logged-in" : "logged-out";

const resolveDeviceType = () => {
  if (typeof window === "undefined") return "desktop";
  if (window.matchMedia?.("(max-width: 767px)").matches) return "mobile";
  if (window.matchMedia?.("(max-width: 1023px)").matches) return "tablet";
  return "desktop";
};

const normalizeItems = (items = []) => (Array.isArray(items) ? items.filter(Boolean) : []);

const normalizeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const safeParseJSON = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const readStore = (storageType) => {
  if (typeof window === "undefined") return EMPTY_PROMOTION_STORE;
  const storage = storageType === "local" ? window.localStorage : window.sessionStorage;
  const storageKey = storageType === "local" ? LOCAL_STORE_KEY : SESSION_STORE_KEY;

  try {
    const parsed = safeParseJSON(storage.getItem(storageKey));
    return {
      impressions: normalizeObject(parsed?.impressions),
      dismissals: normalizeObject(parsed?.dismissals),
    };
  } catch {
    return EMPTY_PROMOTION_STORE;
  }
};

const writeStore = (storageType, nextStore) => {
  if (typeof window === "undefined") return;
  const storage = storageType === "local" ? window.localStorage : window.sessionStorage;
  const storageKey = storageType === "local" ? LOCAL_STORE_KEY : SESSION_STORE_KEY;
  try {
    storage.setItem(storageKey, JSON.stringify(nextStore || EMPTY_PROMOTION_STORE));
  } catch {
    // ignore storage failures
  }
};

const createSessionId = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `promo_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const getOrCreateClientSessionId = () => {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (existing) return existing;
    const generated = createSessionId();
    window.sessionStorage.setItem(SESSION_ID_KEY, generated);
    return generated;
  } catch {
    return createSessionId();
  }
};

const normalizeIdentityValue = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .slice(0, 160);

const resolveActorKey = (visitorId = "") => {
  const user = getStoredUser() || {};
  const session = getActiveSession() || {};
  const identity =
    user?._id ||
    user?.id ||
    user?.customerId ||
    user?.primaryEmail ||
    user?.primaryPhone ||
    session?.email ||
    session?.phone ||
    "";

  if (identity) {
    return `user:${normalizeIdentityValue(identity)}`;
  }

  if (visitorId) {
    return `visitor:${normalizeIdentityValue(visitorId)}`;
  }

  return "visitor:anonymous";
};

const buildStorePayload = (value = {}) => ({
  impressions: normalizeObject(value?.impressions),
  dismissals: normalizeObject(value?.dismissals),
});

const resolvePromotionTargetHref = (promotion = {}) =>
  String(
    promotion?.cta?.href || promotion?.target?.href || promotion?.targetSnapshot?.href || "",
  ).trim();

const groupPromotionsByPlacement = (items = []) =>
  normalizeItems(items).reduce(
    (acc, item) => {
      const placement = item?.placement;
      if (!placement || !acc[placement]) return acc;
      acc[placement] = [...acc[placement], item];
      return acc;
    },
    { ...EMPTY_PLACEMENTS },
  );

export function WebsitePromotionsProvider({ children, enabled = true }) {
  const location = useLocation();
  const initialBootstrap = useHomepageBootstrap(location.pathname);
  const hydrating = useHomepageHydration();
  const firstPaintRef = useRef(initialBootstrap?.rendered ? initialBootstrap : null);
  const { language } = useLanguage();
  const {
    status: websiteConfigStatus,
    promotionsRollout,
  } = useWebsiteConfig();
  const [deviceType, setDeviceType] = useState(() => resolveDeviceType());
  const [authState, setAuthState] = useState(() => resolveAuthState());
  const [visitorId, setVisitorId] = useState(() => (enabled ? getOrCreateVisitorId() : ""));
  const [clientSessionId, setClientSessionId] = useState(() =>
    enabled ? getOrCreateClientSessionId() : ""
  );
  const [dismissedIds, setDismissedIds] = useState({});
  const [deliveryStore, setDeliveryStore] = useState(() => ({
    local: readStore("local"),
    session: readStore("session"),
  }));
  const [rotationStoreSnapshot, setRotationStoreSnapshot] = useState(() => ({
    local: readRotationStore("local"),
    session: readRotationStore("session"),
  }));
  const [state, setState] = useState(() => ({
    status: enabled && language === "en" && initialBootstrap ? "ready" : "idle",
    items: enabled && language === "en" ? initialBootstrap?.promotions?.items || [] : [],
    pagePath: location.pathname,
    error: null,
    audienceContext: {
      deviceType: resolveDeviceType(),
      authState: resolveAuthState(),
      visitorState: "",
    },
  }));
  const viewedEventKeysRef = useRef(new Set());
  const impressionPageRef = useRef(location.pathname);
  if (impressionPageRef.current !== location.pathname) {
    viewedEventKeysRef.current.clear();
    impressionPageRef.current = location.pathname;
  }

  const pagePath = location.pathname || "/";
  const actorKey = resolveActorKey(visitorId);
  const buildViewImpressionKey = useCallback(
    (promotion) =>
      `${pagePath}:${promotion?.placement || ""}:${getCampaignKey(promotion)}:impression`,
    [pagePath],
  );
  const refreshRotationSnapshot = useCallback(() => {
    setRotationStoreSnapshot({
      local: readRotationStore("local"),
      session: readRotationStore("session"),
    });
  }, []);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    const handleResize = () => setDeviceType(resolveDeviceType());
    const handleAuthChange = () => setAuthState(resolveAuthState());
    const handleStorage = () => {
      setAuthState(resolveAuthState());
      setVisitorId(getOrCreateVisitorId());
      setClientSessionId(getOrCreateClientSessionId());
      setDeliveryStore({
        local: readStore("local"),
        session: readStore("session"),
      });
      refreshRotationSnapshot();
    };

    handleResize();
    handleAuthChange();
    window.addEventListener("resize", handleResize);
    window.addEventListener(AUTH_EVENT, handleAuthChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener(AUTH_EVENT, handleAuthChange);
      window.removeEventListener("storage", handleStorage);
    };
  }, [enabled, refreshRotationSnapshot]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;
    setVisitorId(getOrCreateVisitorId());
    setClientSessionId(getOrCreateClientSessionId());
    setDismissedIds({});
    refreshRotationSnapshot();
    return undefined;
  }, [enabled, pagePath, refreshRotationSnapshot]);

  const writeDeliveryStore = useCallback((storageType, nextStore) => {
    const payload = buildStorePayload(nextStore);
    writeStore(storageType, payload);
    setDeliveryStore((prev) => ({
      ...prev,
      [storageType]: payload,
    }));
  }, []);

  const recordRotationExposure = useCallback(
    (promotion) => {
      if (!getPromotionId(promotion)) return;
      const nextStores = recordRotatableItemExposure(promotion, {
        surfaceKey: `promotion:${promotion?.placement || "unknown"}:${pagePath || "/"}`,
        actorKey,
        localStore: readRotationStore("local"),
        sessionStore: readRotationStore("session"),
        windowDays: DEFAULT_ROTATION_WINDOW_DAYS,
      });
      writeRotationStore("local", nextStores.local);
      writeRotationStore("session", nextStores.session);
    },
    [actorKey, pagePath],
  );

  useEffect(() => {
    if (!enabled || !visitorId || !clientSessionId || websiteConfigStatus !== "ready") {
      return undefined;
    }
    if (
      !promotionsRollout?.enabled ||
      (!promotionsRollout?.pageBannerEnabled &&
        !promotionsRollout?.slideUpEnabled &&
        !promotionsRollout?.modalEnabled)
    ) {
      setState({
        status: "ready",
        items: [],
        pagePath,
        error: null,
        audienceContext: {
          deviceType,
          authState,
          visitorState: "",
        },
      });
      return undefined;
    }

    const controller = new AbortController();
    setState((prev) => ({
      ...prev,
      status: "loading",
      items: prev.pagePath === pagePath ? prev.items : [],
      pagePath,
      error: null,
    }));

    fetchWebsitePromotions(
      {
        locale: language || "en",
        pagePath,
        deviceType,
        authState,
        visitorId,
        limit: 12,
      },
      { signal: controller.signal },
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        setState({
          status: "ready",
          items: normalizeItems(response?.items),
          pagePath,
          error: null,
          audienceContext: {
            deviceType: response?.audienceContext?.deviceType || deviceType,
            authState: response?.audienceContext?.authState || authState,
            visitorState: response?.audienceContext?.visitorState || "",
          },
        });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setState((prev) => ({
          status: "error",
          items: prev.pagePath === pagePath ? prev.items : [],
          pagePath,
          error: error?.message || "Unable to load promotions.",
          audienceContext: {
            deviceType,
            authState,
            visitorState: "",
          },
        }));
      });

    return () => controller.abort();
  }, [
    authState,
    clientSessionId,
    deviceType,
    enabled,
    language,
    pagePath,
    promotionsRollout,
    visitorId,
    websiteConfigStatus,
  ]);

  const emitPromotionEvent = useCallback(
    (promotion, eventType, metadata = {}) => {
      if (!getPromotionId(promotion)) return;
      trackWebsitePromotionEvent({
        promotionId: getPromotionId(promotion),
        eventType,
        placement: promotion?.placement || "",
        locale: promotion?.locale || "en",
        visitorId,
        userKey: actorKey,
        clientSessionId,
        pagePath,
        targetHref: resolvePromotionTargetHref(promotion),
        deviceType: state.audienceContext?.deviceType || deviceType,
        authState: state.audienceContext?.authState || authState,
        visitorState: state.audienceContext?.visitorState || "",
        metadata,
      }).catch(() => null);
    },
    [
      actorKey,
      authState,
      clientSessionId,
      deviceType,
      pagePath,
      state.audienceContext,
      visitorId,
    ],
  );

  const recordPromotionImpression = useCallback(
    (promotion, metadata = {}) => {
      if (!getPromotionId(promotion)) return;
      const eventKey = buildViewImpressionKey(promotion);
      if (viewedEventKeysRef.current.has(eventKey)) return;
      if (isPromotionDismissed(promotion, dismissedIds, deliveryStore)) return;
      if (isPromotionFrequencyCapped(promotion, actorKey, deliveryStore)) return;

      viewedEventKeysRef.current.add(eventKey);

      const frequencyCap = promotion?.frequencyCap || {};
      if (frequencyCap.mode !== "none") {
        const storageType = getFrequencyStoreType(promotion);
        const store = storageType === "session" ? deliveryStore.session : deliveryStore.local;
        const key = getFrequencyKey(promotion, actorKey);
        const nextTimestamps = [
          ...normalizeTimestamps(store?.impressions?.[key], resolveFrequencyWindowMs(promotion)),
          Date.now(),
        ].slice(-100);

        writeDeliveryStore(storageType, {
          ...store,
          impressions: {
            ...normalizeObject(store?.impressions),
            [key]: nextTimestamps,
          },
        });
      }

      emitPromotionEvent(promotion, "impression", metadata);
      recordRotationExposure(promotion);
    },
    [
      actorKey,
      buildViewImpressionKey,
      deliveryStore,
      dismissedIds,
      emitPromotionEvent,
      recordRotationExposure,
      writeDeliveryStore,
    ],
  );

  const dismissPromotion = useCallback(
    (promotionOrId, metadata = {}) => {
      const promotion =
        typeof promotionOrId === "string"
          ? normalizeItems(state.items).find((item) => getPromotionId(item) === promotionOrId)
          : promotionOrId;
      const promotionId = getPromotionId(promotion);
      if (!promotionId) return;
      const transientOnly = Boolean(metadata?.transientOnly);
      const eventMetadata = transientOnly
        ? Object.fromEntries(
            Object.entries(metadata || {}).filter(([key]) => key !== "transientOnly"),
          )
        : metadata;

      setDismissedIds((prev) => ({
        ...prev,
        [promotionId]: {
          campaignKey: getCampaignKey(promotion),
          dismissedAt: Date.now(),
        },
      }));

      if (transientOnly) {
        return;
      }

      const dismissBehavior = promotion?.dismissBehavior || {};
      const dismissKey = getDismissKey(promotion);

      if (dismissBehavior.mode === "session") {
        writeDeliveryStore("session", {
          ...deliveryStore.session,
          dismissals: {
            ...normalizeObject(deliveryStore.session?.dismissals),
            [dismissKey]: {
              dismissedAt: Date.now(),
            },
          },
        });
      }

      if (dismissBehavior.mode === "days") {
        writeDeliveryStore("local", {
          ...deliveryStore.local,
          dismissals: {
            ...normalizeObject(deliveryStore.local?.dismissals),
            [dismissKey]: {
              dismissedAt: Date.now(),
              expiresAt:
                Date.now() +
                Math.max(Number(dismissBehavior?.days || 7), 1) * DAY_IN_MS,
            },
          },
        });
      }

      if (dismissBehavior.mode === "campaign") {
        writeDeliveryStore("local", {
          ...deliveryStore.local,
          dismissals: {
            ...normalizeObject(deliveryStore.local?.dismissals),
            [dismissKey]: {
              dismissedAt: Date.now(),
              campaignKey: getCampaignKey(promotion),
            },
          },
        });
      }

      emitPromotionEvent(promotion, "dismiss", eventMetadata);
    },
    [deliveryStore.local, deliveryStore.session, emitPromotionEvent, state.items, writeDeliveryStore],
  );

  const trackPromotionClick = useCallback(
    (promotion, metadata = {}) => {
      if (!getPromotionId(promotion)) return;
      emitPromotionEvent(promotion, "click", metadata);
    },
    [emitPromotionEvent],
  );

  const canShowPromotion = useCallback(
    (promotion) => {
      if (isPromotionDismissed(promotion, dismissedIds, deliveryStore)) return false;
      if (viewedEventKeysRef.current.has(buildViewImpressionKey(promotion))) return true;
      return !isPromotionFrequencyCapped(promotion, actorKey, deliveryStore);
    },
    [actorKey, buildViewImpressionKey, deliveryStore, dismissedIds],
  );

  const placements = useMemo(() => groupPromotionsByPlacement(state.items), [state.items]);

  const visiblePlacements = useMemo(() => {
    const next = { ...EMPTY_PLACEMENTS };
    Object.entries(placements).forEach(([placement, items]) => {
      if (!isPromotionPlacementEnabled(promotionsRollout, placement)) {
        next[placement] = [];
        return;
      }
      const eligible = normalizeItems(items).filter((item) => canShowPromotion(item));
      if (hydrating) {
        next[placement] = eligible;
        return;
      }
      const ordered = orderRotatableItemsForVisitor(eligible, {
        surfaceKey: `promotion:${placement}:${pagePath || "/"}`,
        actorKey,
        localStore: rotationStoreSnapshot.local,
        sessionStore: rotationStoreSnapshot.session,
        windowDays: DEFAULT_ROTATION_WINDOW_DAYS,
      });
      // Keep the already-painted public slide first during this page visit.
      // The live response still controls eligibility, changes and removals.
      const firstPaint = firstPaintRef.current;
      const firstId = placement === "page-banner" && firstPaint?.pathname === pagePath
        ? getPromotionId(firstPaint.promotions?.items?.[0]) : "";
      const first = ordered.find(item => getPromotionId(item) === firstId);
      next[placement] = first ? [first, ...ordered.filter(item => item !== first)] : ordered;
    });
    return next;
  }, [actorKey, canShowPromotion, hydrating, pagePath, placements, promotionsRollout, rotationStoreSnapshot]);

  const value = useMemo(
    () => ({
      status: state.status,
      loading: state.status === "loading",
      error: state.error,
      pagePath,
      visitorId,
      audienceContext: state.audienceContext,
      items: state.items,
      placements,
      pageBannerPromotions: visiblePlacements["page-banner"],
      pageBannerPromotion: visiblePlacements["page-banner"][0] || null,
      slideUpPromotion: visiblePlacements["slide-up"][0] || null,
      modalPromotion: visiblePlacements["modal-popup"][0] || null,
      recordPromotionImpression,
      trackPromotionClick,
      dismissPromotion,
    }),
    [
      dismissPromotion,
      pagePath,
      placements,
      recordPromotionImpression,
      state,
      trackPromotionClick,
      visiblePlacements,
      visitorId,
    ],
  );

  return (
    <WebsitePromotionsContext.Provider value={value}>
      {children}
    </WebsitePromotionsContext.Provider>
  );
}

export function useWebsitePromotions() {
  const context = useContext(WebsitePromotionsContext);
  if (!context) {
    throw new Error("useWebsitePromotions must be used within a WebsitePromotionsProvider");
  }
  return context;
}
